import React from "react";
import { Link } from "react-router-dom";
import { CloudLightning, ExternalLink } from "lucide-react";
import { LANDFALL, OBSERVATIONS, SOURCES, SURVEY_NOTE } from "../data/isaiasFacts";

const H = "'Anton','Oswald',sans-serif";

const Src = ({ id }) => (
  <a
    href={SOURCES[id].url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-white/50 hover:text-[#E11D26] text-[11px] uppercase tracking-wider transition-colors"
    title={SOURCES[id].label}
  >
    {SOURCES[id].short} <ExternalLink className="h-3 w-3" />
  </a>
);

// Brief dated paragraph used on topic (spoke) pages.
export const StormFactsBrief = ({ city, stormHub }) => (
  <section className="py-8 bg-[#0A0A0A] border-b border-white/10" data-testid="storm-facts-brief">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <p className="text-white/75 text-sm sm:text-base leading-relaxed">
        <span className="text-[#E11D26] font-bold uppercase tracking-wider text-xs mr-2">Hurricane Isaias</span>
        Hurricane Isaias made landfall {LANDFALL.location} at {LANDFALL.time} on{" "}
        <time dateTime={LANDFALL.isoDate}>{LANDFALL.dateLabel}</time> as a {LANDFALL.category} hurricane with{" "}
        {LANDFALL.sustainedWinds} sustained winds (National Hurricane Center). {city === "Pensacola"
          ? "The center passed about 40 miles east of Pensacola, placing Escambia and Santa Rosa counties in the storm's strongest western-eyewall winds, with gusts above 100 mph recorded near Santa Rosa Sound and Pensacola."
          : "Okaloosa and Walton counties took the eye and eastern eyewall, with hurricane-force gusts from Fort Walton Beach to Choctaw Beach and more than 9 inches of rain in Shalimar and Valparaiso."}{" "}
        See the full{" "}
        <Link to={stormHub} className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
          Hurricane Isaias {city} storm facts and claim help
        </Link>
        .
      </p>
    </div>
  </section>
);

// Full facts block used on geo + storm hubs.
const StormFacts = ({ city }) => {
  const obs = OBSERVATIONS[city] || [];
  return (
    <section className="py-14 md:py-16 bg-[#0A0A0A] border-b border-white/10" data-testid="storm-facts">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 mb-3">
          <CloudLightning className="h-4 w-4 text-[#E11D26]" />
          <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
            Verified Storm Record · NHC / NWS
          </span>
        </div>
        <h2 className="text-white text-2xl sm:text-3xl font-black uppercase" style={{ fontFamily: H }}>
          Hurricane Isaias Storm Facts — {city}
        </h2>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="bg-[#0F0F0F] border border-white/10 p-6">
            <h3 className="text-white font-bold uppercase tracking-wide text-sm mb-4">Landfall</h3>
            <dl className="space-y-3 text-sm">
              {[
                ["Date", <time dateTime={LANDFALL.isoDate}>{LANDFALL.dateLabel}, {LANDFALL.time}</time>],
                ["Location", LANDFALL.location],
                ["Intensity", `${LANDFALL.category} · ${LANDFALL.sustainedWinds} sustained · ${LANDFALL.pressure}`],
                ["Center position", LANDFALL.centerAtLandfall],
                ["Peak intensity", LANDFALL.peakIntensity],
                ["Wind field", LANDFALL.windField],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-3">
                  <dt className="text-white/50 uppercase tracking-wider text-[11px] pt-0.5">{k}</dt>
                  <dd className="text-white/85 leading-snug">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex gap-4">
              <Src id="nhcLandfall" />
              <Src id="nhcAdv12" />
            </div>
          </div>

          <div className="bg-[#0F0F0F] border border-white/10 p-6">
            <h3 className="text-white font-bold uppercase tracking-wide text-sm mb-4">Warnings &amp; Surge Forecast</h3>
            <dl className="space-y-3 text-sm">
              {[
                ["Warnings", LANDFALL.warnings],
                ["Surge forecast", LANDFALL.surgeForecast],
                ["Aftermath", LANDFALL.postTropical],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-3">
                  <dt className="text-white/50 uppercase tracking-wider text-[11px] pt-0.5">{k}</dt>
                  <dd className="text-white/85 leading-snug">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex gap-4">
              <Src id="nhcAdv12" />
              <Src id="nwsTae" />
            </div>
          </div>
        </div>

        <h3 className="mt-10 text-white font-bold uppercase tracking-wide text-sm">
          Observations near {city}
        </h3>
        <div className="mt-4 border border-white/10 divide-y divide-white/10">
          {obs.map((o, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_auto] sm:grid-cols-[150px_1fr_auto] gap-x-4 gap-y-1 items-center px-4 py-3 text-sm bg-[#0F0F0F]"
              data-testid={`storm-obs-${i}`}
            >
              <span className="text-white/50 uppercase tracking-wider text-[11px] sm:order-1">{o.metric}</span>
              <span className="text-white/85 col-span-2 sm:col-span-1 sm:order-2">
                <strong className="text-white">{o.value}</strong> — {o.place}
                {o.preliminary && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400/90 border border-amber-400/30 px-1.5 py-0.5">
                    Preliminary
                  </span>
                )}
              </span>
              <span className="sm:order-3 justify-self-end"><Src id={o.source} /></span>
            </div>
          ))}
        </div>

        <p className="mt-5 text-white/50 text-xs leading-relaxed">
          {SURVEY_NOTE} Point observations rarely sample the true peak of an event; damage to
          your property is documented on-site, not inferred from these readings.
        </p>
      </div>
    </section>
  );
};

export default StormFacts;
